import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, getLatestArticles, getArticlesByCategory } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
              文章未找到
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              抱歉，您访问的文章不存在或已被删除
            </p>
            <button
              onClick={() => {
                navigate('/');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);
  const relatedArticles = getArticlesByCategory(article.categorySlug)
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '俺去也',
      url: 'https://www.anquye.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.anquye.com/article/${article.id}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                <article className="flex-1 max-w-[860px]">
                  <div className="rounded-xl overflow-hidden mb-6">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-[300px] md:h-[400px] object-cover"
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                      {article.category}
                    </span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.publishDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views.toLocaleString()} 次阅读
                    </span>
                  </div>

                  <div
                    className="article-content prose prose-sm md:prose-base max-w-none"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-10 pt-6 border-t border-background-200">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-foreground-600">标签：</span>
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1.5 text-xs bg-background-100 text-foreground-700 rounded-full border border-background-200"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>

                <aside className="lg:w-[360px] flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="bg-background-100 rounded-xl p-5 border border-background-200 mb-5">
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                        <i className="ri-links-line text-primary-500"></i>
                        相关文章
                      </h3>
                      <div className="space-y-4">
                        {relatedArticles.length > 0 ? (
                          relatedArticles.map((rel) => (
                            <div
                              key={rel.id}
                              className="flex gap-3 cursor-pointer group"
                              onClick={() => {
                                navigate(`/article/${rel.id}`);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                            >
                              <div className="w-20 h-14 flex-shrink-0 rounded-lg overflow-hidden">
                                <img
                                  src={rel.coverImage}
                                  alt={rel.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                                  {rel.title}
                                </h4>
                                <span className="text-[11px] text-foreground-500 mt-1 block">
                                  {rel.publishDate}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-foreground-500">暂无相关文章</p>
                        )}
                      </div>
                    </div>

                    <div className="bg-accent-50 rounded-xl p-5 border border-accent-200">
                      <h3 className="text-base font-heading font-bold text-foreground-900 mb-2 flex items-center gap-2">
                        <i className="ri-information-line text-accent-500"></i>
                        关于俺去也
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                        俺去也是深受用户喜爱的娱乐内容平台，海量高清资源免费看，每日更新精彩不断。
                      </p>
                      <button
                        onClick={() => {
                          navigate('/about');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
                      >
                        了解更多 →
                      </button>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}